import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import InputGroup from "react-bootstrap/InputGroup";
import Spinner from "react-bootstrap/Spinner";
import ProgressBar from "react-bootstrap/ProgressBar";
import ListGroup from "react-bootstrap/ListGroup";
import ModalComponentN3 from "../../components/Modal/ModalComponentN3";

import BackendN3API from "../../api/backend/n3";
import CategorySelect from "../../components/forms/CategorySelect";

import { sha256 } from "js-sha256";
import dekkai from "dekkai";
import ReactPaginate from "react-paginate";
import ImportStatusSelect from "../../components/forms/ImportStatusSelect";
import BreadcrumbTwo from "../../components/forms/BreadcrumbTwo";

const only = [
    // 'snp_id',
    "alleles",
    // 'snp_position',
    "variation_type",
    "primer_id",
    // 'sequence',
    // 'product_size',
    // 'start',
    // 'len',
    // 'tm',
    // 'gc',
    // 'any',
    // 'n3seq',
];

const columns = [
    "snp_id",
    "gene",
    "alleles",
    "snp_position",
    "variation_type",
    "primer_id",
    "sequence",
    "product_size",
    "start",
    "len",
    "tm",
    "gc",
    "any",
    "n3seq",
    "rice_cultivar_th", 
    "rice_cultivar_en"
];

const sha256Gen = (values) => {
    const arr = [];

    only.forEach((v) => {
        arr.push(values[v] ? values[v] : null);
    });

    return sha256(JSON.stringify(arr));
};

const parseCSV = async (file, callback) => {
    await dekkai.init(1);

    const table = await dekkai.tableFromLocalFile(file);
    const count = table.rowCount;

    for (let i = 0; i < count; i++) {
        const row = {};
        const raw = await table.getRow(i);

        columns.forEach((v, i) => {
            const value = raw.valueByIndex(i).trim();
            row[v] = value ? value : null;
        });

        row._index = i + 1;
        await callback(row, i, count);
    }

    dekkai.terminate();
};

const Item = memo((props) => {
    const variant = useCallback((status) => {
        switch (status) {
            case 1:
                return "success";
            case 2:
                return "warning";
            case 3:
                return "info";
            default:
                return "danger";
        }
    }, []);

    return (
        <ListGroup.Item
            action
            variant={variant(props?.data?.status)}
            onClick={(e) => {
                e.preventDefault();
                props.onClick?.(props.data);
            }}
        >
            <div className="d-flex gap-2 align-items-center">
                <code>#{props?.data?._index}</code>
                <span className="font-monospace text-truncate">{props?.data?.unique_id}</span>
            </div>
        </ListGroup.Item>
    );
});

const BackendN3ImportPage = () => {
    const state = useRef({
        max: 0,
        now: 0,
        rows: [],
        canceled: false,
        error: null,
        color: "primary",
        interval: null,
        persec: 0,
        lastsec: 0,
        category: "0",
        mounted: false,
        processed: false,
    });

    const [now, setNow] = useState(0);
    const [page, setPage] = useState(0);
    const [category, setCategory] = useState("0");
    const [status, setStatus] = useState("");
    const [dataModal, setDataModal] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleOnSelectFile = useCallback((e) => {
        setLoading(() => true);
        parseCSV(e.target.files[0], (row, index, max) => {
            const temp = {
                ...row,
                category_id: state.current.category,
            };

            if (state.current.canceled) {
                throw new Error("canceled");
            }

            return BackendN3API.create(temp)
                .then((response) => {
                    temp.status = response?.data?.status ?? 0;
                    temp.unique_id = response?.data?.unique_id ?? sha256Gen(row);
                })
                .catch(() => {
                    temp.status = 0;
                    temp.unique_id = sha256Gen(temp);
                })
                .finally(() => {
                    state.current.max = max;
                    state.current.now = index + 1;
                    state.current.rows.push(temp);
                });
        })
            .catch((e) => {
                if (!state.current.canceled) {
                    alert("ไม่สามารถอ่านไฟล์ได้");
                    console.log(e);
                }
            })
            .finally(() => setLoading(() => false));
    }, []);

    const handleClickView = useCallback((data) => {
        setDataModal(() => data);
    }, []);

    const handleCloseModal = useCallback(() => {
        setDataModal(() => null);
    }, []);

    useEffect(() => {
        if (state.current.mounted) {
            if (loading) {
                state.current.color = "primary";
                state.current.interval = setInterval(() => setNow(() => state.current.now), 1000);
            } else {
                clearInterval(state.current.interval);
                setNow(() => state.current.rows.length);
                state.current.color = state.current.rows.length !== state.current.max ? "danger" : "success";
            }
            return () => clearInterval(state.current.interval);
        }
    }, [loading]);

    useEffect(() => {
        if (state.current.mounted) {
            setPage(() => 0);
        }
    }, [status]);

    useEffect(() => {
        state.current.category = category;
    }, [category]);

    useEffect(() => {
        state.current.mounted = true;

        return () => {
            state.current.mounted = false;
            state.current.canceled = true;
            clearInterval(state.current.interval);
        };
    }, []);

    const docs = useMemo(() => {
        let rows = state.current.rows.slice();
        if (status) {
            rows = rows.filter((v) => v.status == status);
        }
        return rows;
    }, [status, now]);

    const rows = useMemo(() => {
        if (page === 0) {
            return docs.slice(-10);
        }
        const start = page * 10;
        const ended = start + 10;
        return docs.slice(-1 * ended, -1 * start);
    }, [page, docs]);

    return (
        <div className="position-relative w-100">
            <BreadcrumbTwo type="n3" names={["จัดการข้อมูล SNP Marker", "นำเข้าข้อมูล"]} />

            <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>เลือกหมวดหมู่ข้อมูล</Form.Label>
                <CategorySelect value={category} onChange={(e) => setCategory(e.target.value)} hideOptionAll disabled={loading} />
            </Form.Group>

            <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>เลือกไฟล์</Form.Label>
                <InputGroup className="mb-3">
                    <Form.Control type="file" accept=".csv" onChange={handleOnSelectFile} disabled={loading} />

                    {loading && (
                        <InputGroup.Text>
                            <Spinner animation="border" size="sm" />
                        </InputGroup.Text>
                    )}
                </InputGroup>
            </Form.Group>

            {state.current.rows.length > 0 && state.current.max > 0 && (
                <>
                    {now}/{state.current.max}
                    <ProgressBar now={now} max={state.current.max} className="mb-3" variant={state.current.color} />
                    <Form.Group controlId="formFile" className="mb-3">
                        <Form.Label>สถานะ</Form.Label>
                        <ImportStatusSelect value={status} onChange={(e) => setStatus(e.target.value)} />
                    </Form.Group>
                    <ListGroup className="mb-3">
                        {rows.reverse().map((v) => (
                            <Item key={v.unique_id} data={v} onClick={handleClickView} />
                        ))}
                    </ListGroup>
                    <ReactPaginate
                        breakLabel="..."
                        nextLabel=">"
                        forcePage={page}
                        onPageChange={(v) => setPage(v.selected)}
                        pageRangeDisplayed={5}
                        pageCount={Math.ceil(docs.length / 10)}
                        previousLabel="<"
                        renderOnZeroPageCount={null}
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                        containerClassName="pagination"
                        activeClassName="active"
                    />
                </>
            )}

            <Modal show={Boolean(dataModal)} onHide={handleCloseModal} fullscreen centered>
                <Modal.Header closeButton>
                    <code className="fs-5 text-truncate">{dataModal?.unique_id}</code>
                </Modal.Header>
                <Modal.Body>
                    <ModalComponentN3 data={dataModal} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}>
                        ปิด
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BackendN3ImportPage;
